import {showMessage} from 'exsportia-components'
import {useCallback} from 'react'

import {BookingStateParticipant} from '../services/booking/forms/types'
import {useLocale} from '../utils/i18n'
import {
  buildPassNotValidMessage,
  getPassNotValidFromError,
  hasPass,
  PassNotValidMessageKey,
  removePassesFromParticipants,
} from './pass-not-valid'
import {usePassHoursOptions} from './use-pass-hours-options'

type SetFieldValue = (field: string, value: unknown) => unknown

export type PassNotValidContext = {
  participants: BookingStateParticipant[]
  // Multi-session class bookings keep their own participants (and "share the pass" switch) per session.
  perSessionParticipants?: Record<string, BookingStateParticipant[]>
  perSessionUseForParticipants?: Record<string, boolean>
  setFieldValue: SetFieldValue
}

/**
 * For the create-booking forms' error paths: if the refusal is 409 PASS_NOT_VALID_AT_THIS_TIME, say
 * so in one line, take the named pass off the participants (the summary page re-quotes from the
 * form values, so the customer sees the full price) and return true. Anything else returns false and
 * is left to the caller. Never retries.
 */
export const usePassNotValidHandler = () => {
  const {l} = useLocale()
  const hoursOptions = usePassHoursOptions()
  return useCallback(
    (
      error: unknown,
      {participants, perSessionParticipants, perSessionUseForParticipants, setFieldValue}: PassNotValidContext,
    ) => {
      const refusal = getPassNotValidFromError(error)
      if (!refusal) {
        return false
      }
      const {passes} = refusal
      const main = removePassesFromParticipants(participants, passes)
      if (main.removed > 0) {
        void setFieldValue('participants', main.participants)
        if (!main.participants.some(hasPass)) {
          void setFieldValue('useForParticipants', false)
        }
      }
      if (perSessionParticipants) {
        const nextPerSession: Record<string, BookingStateParticipant[]> = {}
        const nextUseFor = {...perSessionUseForParticipants}
        let changed = false
        Object.entries(perSessionParticipants).forEach(([sessionId, list]) => {
          const result = removePassesFromParticipants(list, passes)
          nextPerSession[sessionId] = result.participants
          if (result.removed > 0) {
            changed = true
            if (!result.participants.some(hasPass)) {
              nextUseFor[sessionId] = false
            }
          }
        })
        if (changed) {
          void setFieldValue('perSessionParticipants', nextPerSession)
          void setFieldValue('perSessionUseForParticipants', nextUseFor)
        }
      }
      showMessage(
        buildPassNotValidMessage(passes, (key: PassNotValidMessageKey) => l(key), hoursOptions),
        'error',
      )
      return true
    },
    [l, hoursOptions],
  )
}
